import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useParams } from 'react-router';
import {
  LEAD_QUALIFICATION_FIELDS,
  leadPushBlockers,
  suggestDealName,
  type LeadField,
} from '@wr/shared';
import { useToast } from '../../components/Toast.js';
import { Dialog, Notice, TextAreaField } from '../../components/ui.js';
import { LISTS } from '../../lists.js';
import { useOffline } from '../../offline/context.js';
import { newId } from '../../offline/ids.js';
import type { LocalRecord } from '../../offline/store.js';
import { useSignedIn } from '../../session.js';
import { fullName } from '../people/data.js';
import { loadOwners, type LeadSheetData, type Owner } from './data.js';
import { useLead, useLeadRights, usePeopleBack } from './LeadSheet.js';
import { ProjectBlock, focusFirst, projectErrors, type ProjectErrors } from './Project.js';
import { stageFor, stageName, useStages } from './stages.js';

const str = (v: unknown) => (typeof v === 'string' ? v : '');

/** The fields the lead form edits, as text (applications as a list). */
type Draft = {
  company_name: string;
  country: string;
  applications: string[];
  note: string;
  event_id: string;
  deal_name: string;
  installation_country: string;
  owner_user_id: string;
  stage_id: string;
  deal_market: string;
  role: string;
  pain: string;
  product_fit: string;
  qualification: string;
  project_call_date: string;
  disqualified_why: string;
  disqualified_when: string;
};

const FIELDS = [
  'company_name',
  'country',
  'applications',
  'note',
  'event_id',
  'deal_name',
  'installation_country',
  'owner_user_id',
  'stage_id',
  'deal_market',
  'role',
  'pain',
  'product_fit',
  'qualification',
  'project_call_date',
  'disqualified_why',
  'disqualified_when',
] as const satisfies readonly (keyof Draft & LeadField)[];

function draftOf(l: LocalRecord): Draft {
  return {
    company_name: str(l.company_name),
    country: str(l.country),
    applications: Array.isArray(l.applications) ? (l.applications as string[]) : [],
    note: str(l.note),
    event_id: str(l.event_id),
    deal_name: str(l.deal_name),
    installation_country: str(l.installation_country),
    owner_user_id: str(l.owner_user_id),
    stage_id: str(l.stage_id),
    deal_market: str(l.deal_market),
    role: str(l.role),
    pain: str(l.pain),
    product_fit: str(l.product_fit),
    qualification: str(l.qualification),
    project_call_date: str(l.project_call_date).slice(0, 10),
    disqualified_why: str(l.disqualified_why),
    disqualified_when: str(l.disqualified_when),
  };
}

export function LeadForm() {
  const { id = '' } = useParams();
  const { close, open } = usePeopleBack();
  const { data, error } = useLead(id);
  const rights = useLeadRights(data?.lead ?? null);
  if (error || !data) {
    return (
      <Dialog title="Edit lead" onClose={close} className="wide">
        {error ? (
          <Notice kind="error">{error}</Notice>
        ) : (
          <div className="spinner" aria-label="Loading" />
        )}
      </Dialog>
    );
  }
  if (!rights.change) {
    return (
      <Dialog title="Edit lead" onClose={close} className="wide">
        <Notice kind="error">You can read this lead, not change it.</Notice>
      </Dialog>
    );
  }
  return <Form key={id} data={data} onDone={() => open(`/people/leads/${id}`)} />;
}

/**
 * Edit lead (brief section 6.8, decision of Nov 2, 2026): block 2 first,
 * the same as everywhere (the four answers, then the Discovery Capture);
 * then, folded, the company and the deal as docs/prototype.html draws them
 * (company name, country, operating markets; contacts, read only; note,
 * event ID, deal name, owner, stage, market, with the copy-from-company
 * links and the deal name suggested as Lead Entry). The four answers cannot
 * be removed. What is still missing before HubSpot can be completed later
 * (To do); only what changed is saved, on the device first.
 */
function Form({ data, onDone }: { data: LeadSheetData; onDone(): void }) {
  const { user, can } = useSignedIn();
  const { save, store } = useOffline();
  const toast = useToast();
  const formId = useId();
  const before = draftOf(data.lead);
  const [draft, setDraft] = useState<Draft>(() => {
    const d = { ...before };
    // Suggested: the company's name, and the first operating market.
    if (!d.deal_name) d.deal_name = suggestDealName(d.company_name, data.eventCode);
    if (!d.deal_market && d.applications[0]) d.deal_market = d.applications[0];
    if (!d.owner_user_id) d.owner_user_id = user.id;
    // A lead of before Nov 2, 2026 may have no installation country: the company's, to confirm.
    if (!d.installation_country) d.installation_country = d.country;
    return d;
  });
  const [errors, setErrors] = useState<Partial<Record<keyof Draft, string>>>({});
  const [projectErrs, setProjectErrs] = useState<ProjectErrors>({});
  const [dealOpen, setDealOpen] = useState(false);
  const rootRef = useRef<HTMLFormElement>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [owners, setOwners] = useState<Owner[] | null>(null);
  const [events, setEvents] = useState<LocalRecord[]>([]);
  const qualify = can('leads.qualify');
  const stages = useStages();
  // A stage HubSpot no longer has still shows, by its name.
  const stageOptions =
    draft.stage_id && !stages.some((s) => s.id === draft.stage_id)
      ? [...stages, { id: draft.stage_id, label: stageName(draft.stage_id, stages) }]
      : stages;

  useEffect(() => {
    let current = true;
    void loadOwners()
      .catch(() => null)
      .then((list) => current && setOwners(list));
    void store?.listRecords('event').then((list) => current && setEvents(list));
    return () => {
      current = false;
    };
  }, [store]);

  const set = <K extends keyof Draft>(field: K, value: Draft[K]) => {
    setDraft((d) => {
      const next = { ...d, [field]: value };
      // The stage follows the qualification (MQLs, or Marketing nurturing).
      if (field === 'qualification') next.stage_id = stageFor(value, stages) ?? d.stage_id;
      return next;
    });
    setErrors((e) => ({ ...e, [field]: undefined }));
  };

  const eventCode = (eventId: string) =>
    eventId === str(data.lead.event_id)
      ? data.eventCode
      : ((events.find((e) => e.id === eventId)?.event_code as string | undefined) ?? null);

  const ownerList: Owner[] = owners ?? [
    { id: user.id, name: `${user.firstName} ${user.lastName}` },
    ...(before.owner_user_id && before.owner_user_id !== user.id
      ? [{ id: before.owner_user_id, name: data.ownerName ?? 'Current owner' }]
      : []),
  ];
  const eventOptions = events
    .filter((e) => e.status !== 'completed' || e.id === draft.event_id)
    .sort((a, b) => str(a.event_code).localeCompare(str(b.event_code)));
  if (draft.event_id && !eventOptions.some((e) => e.id === draft.event_id)) {
    eventOptions.unshift({ id: draft.event_id, event_code: data.eventCode ?? 'Current event' });
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (saving) return;
    const answers = projectErrors(draft);
    const next: typeof errors = {};
    if (!draft.company_name.trim()) next.company_name = 'Company name is required.';
    setProjectErrs(answers ?? {});
    setErrors(next);
    if (answers || Object.keys(next).length > 0) {
      setFailed('The four answers and the company name cannot be removed.');
      if (answers) focusFirst(rootRef.current, answers);
      else setDealOpen(true);
      return;
    }
    const fields: Record<string, unknown> = {};
    for (const f of FIELDS) {
      if (!qualify && (LEAD_QUALIFICATION_FIELDS as readonly string[]).includes(f)) continue;
      const was = before[f];
      const now = draft[f];
      if (JSON.stringify(was) === JSON.stringify(now)) continue;
      fields[f] = Array.isArray(now) ? now : now.trim() === '' ? null : now.trim();
    }
    // Where from: Tradeshow with an event, Email without (brief section 3).
    if ('event_id' in fields) fields.where_from = fields.event_id ? 'Tradeshow' : 'Email';
    if (Object.keys(fields).length === 0) return onDone();
    setSaving(true);
    try {
      await save({
        opId: newId(),
        entity: 'lead',
        id: data.lead.id,
        action: 'upsert',
        changedAt: new Date().toISOString(),
        fields,
      });
      const left = leadPushBlockers({ ...data.lead, ...fields });
      toast(
        left.length === 0
          ? 'Lead saved. It goes to HubSpot by itself.'
          : `Lead saved. ${left.length} thing${left.length > 1 ? 's' : ''} to complete before HubSpot.`,
      );
      onDone();
    } catch {
      setFailed('This device could not save the lead. Nothing was lost: try again.');
      setSaving(false);
    }
  }

  return (
    <Dialog
      title="Edit lead"
      onClose={onDone}
      className="wide"
      footer={
        <>
          <button type="button" className="btn" onClick={onDone}>
            Cancel
          </button>
          <button type="submit" form={formId} className="btn primary" disabled={saving}>
            Save lead
          </button>
        </>
      }
    >
      <form
        id={formId}
        ref={rootRef}
        className="dlg-form"
        noValidate
        onSubmit={(e) => void submit(e)}
      >
        <ProjectBlock
          value={draft}
          onChange={(v) => {
            setDraft((d) => {
              const next = { ...d, ...v };
              // The stage follows the outcome (MQLs, or Marketing nurturing).
              if (v.qualification !== d.qualification) {
                next.stage_id = stageFor(v.qualification, stages) ?? d.stage_id;
              }
              return next;
            });
            setProjectErrs({});
            setFailed(null);
          }}
          errors={projectErrs}
          personName={data.contacts[0] ? str(data.contacts[0].first_name) : undefined}
        />
        <details
          className="k-more"
          open={dealOpen}
          onToggle={(e) => setDealOpen((e.currentTarget as HTMLDetailsElement).open)}
        >
          <summary>Company and deal (HubSpot)</summary>
          <div className="k-more-body">
            <div className="formsec">
              <h3>Company</h3>
              <span className="note" style={{ marginTop: -10 }}>
                Will be created or matched in HubSpot
              </span>
              <LeField label="Company name" required error={errors.company_name}>
                {(id) => (
                  <input
                    id={id}
                    type="text"
                    value={draft.company_name}
                    onChange={(e) => set('company_name', e.target.value)}
                  />
                )}
              </LeField>
              <LeField label="Country" required error={errors.country}>
                {(id) => (
                  <CountrySelect
                    id={id}
                    value={draft.country}
                    onChange={(v) => set('country', v)}
                  />
                )}
              </LeField>
              <div className="le-f">
                <span className="le-l">Operating market(s)</span>
                <div className="chips" role="group" aria-label="Operating market(s)">
                  {LISTS.markets.map((m) => {
                    const on = draft.applications.includes(m.code);
                    return (
                      <button
                        key={m.code}
                        type="button"
                        className={`chip le-chip${on ? ' on' : ''}`}
                        aria-pressed={on}
                        onClick={() =>
                          set(
                            'applications',
                            LISTS.markets
                              .map((x) => x.code)
                              .filter((c) => (c === m.code ? !on : draft.applications.includes(c))),
                          )
                        }
                      >
                        {m.code}
                      </button>
                    );
                  })}
                </div>
                {errors.applications && <small className="err">{errors.applications}</small>}
              </div>
            </div>

            <div className="formsec">
              <h3>Contacts</h3>
              <p style={{ margin: 0 }}>
                {data.contacts.length === 0 ? (
                  <span className="note">No contact linked</span>
                ) : (
                  data.contacts.map((c, i) => (
                    <span key={c.id}>
                      {i > 0 && <br />}
                      <b>{fullName(c) || 'Contact'}</b>
                      {c.is_primary ? ' (primary)' : ''}
                      {c.email ? `, ${c.email}` : ''}
                    </span>
                  ))
                )}
              </p>
              <p className="note" style={{ margin: 0 }}>
                Add or change contacts on the lead sheet. Their details are edited on each contact.
              </p>
            </div>

            <div className="formsec">
              <h3>Deal</h3>
              <span className="note" style={{ marginTop: -10 }}>
                Automatically linked to the contact above
              </span>
              <TextAreaField
                label="Note (optional)"
                placeholder="Conversation context, remarks..."
                value={draft.note}
                onChange={(e) => set('note', e.target.value)}
              />
              <LeField label="Event ID">
                {(id) => (
                  <select
                    id={id}
                    value={draft.event_id}
                    onChange={(e) => {
                      const value = e.target.value;
                      setDraft((d) => {
                        const old = suggestDealName(d.company_name, eventCode(d.event_id));
                        const renamed = !d.deal_name || d.deal_name === old;
                        return {
                          ...d,
                          event_id: value,
                          deal_name: renamed
                            ? suggestDealName(d.company_name, eventCode(value))
                            : d.deal_name,
                        };
                      });
                    }}
                  >
                    <option value="">No event (Email)</option>
                    {eventOptions.map((ev) => (
                      <option key={ev.id} value={ev.id}>
                        {str(ev.event_code)}
                      </option>
                    ))}
                  </select>
                )}
              </LeField>
              <LeField label="Deal name" required>
                {(id) => (
                  <input
                    id={id}
                    type="text"
                    value={draft.deal_name}
                    onChange={(e) => set('deal_name', e.target.value)}
                  />
                )}
              </LeField>
              <div className="g2">
                <LeField label="Deal owner" required>
                  {(id) => (
                    <select
                      id={id}
                      value={draft.owner_user_id}
                      onChange={(e) => set('owner_user_id', e.target.value)}
                    >
                      {ownerList.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.name}
                        </option>
                      ))}
                    </select>
                  )}
                </LeField>
                <LeField label="Deal stage" required>
                  {(id) => (
                    <select
                      id={id}
                      value={draft.stage_id}
                      onChange={(e) => set('stage_id', e.target.value)}
                    >
                      <option value="">Select a stage</option>
                      {stageOptions.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.label}
                        </option>
                      ))}
                    </select>
                  )}
                </LeField>
              </div>
              <LeField
                label="Deal market"
                required
                copy={{
                  label: 'copy from company',
                  onClick: () => draft.applications[0] && set('deal_market', draft.applications[0]),
                }}
              >
                {(id) => (
                  <select
                    id={id}
                    value={draft.deal_market}
                    onChange={(e) => set('deal_market', e.target.value)}
                  >
                    <option value="">Select a market</option>
                    {LISTS.markets.map((m) => (
                      <option key={m.code} value={m.code}>
                        {m.code}
                      </option>
                    ))}
                  </select>
                )}
              </LeField>
              <div className="auto-badge">
                ✓ Pipeline = Sales Pipeline, Where from = {draft.event_id ? 'Tradeshow' : 'Email'},
                Last seen = {eventCode(draft.event_id) ?? '(no event)'}
              </div>
            </div>
          </div>
        </details>
        {failed && <Notice kind="error">{failed}</Notice>}
      </form>
    </Dialog>
  );
}

/** A Lead Entry field (prototype: .le-f, .le-l, the red star and the ↻ copy link). */
function LeField({
  label,
  required,
  error,
  copy,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string;
  copy?: { label: string; onClick(): void };
  children(id: string): ReactNode;
}) {
  const id = useId();
  return (
    <div className={`le-f${error ? ' error' : ''}`}>
      <label className="le-l" htmlFor={id}>
        {label}
        {required && (
          <>
            {' '}
            <span className="req">*</span>
          </>
        )}
        {copy && (
          <>
            {' '}
            <button type="button" className="sync-link" onClick={copy.onClick}>
              ↻ {copy.label}
            </button>
          </>
        )}
      </label>
      {children(id)}
      {error && <div className="le-err">{error}</div>}
    </div>
  );
}

function CountrySelect({
  id,
  value,
  onChange,
}: {
  id: string;
  value: string;
  onChange(value: string): void;
}) {
  return (
    <select id={id} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">Select a country</option>
      {LISTS.countries.map((c) => (
        <option key={c} value={c}>
          {c}
        </option>
      ))}
    </select>
  );
}
